import {
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"
import { useTranslation } from "react-i18next"

import { api } from "@/api"
import ENDPOINTS from "@/constants/endpoints"
import { useToast } from "@/hooks/use-toast"

type RulePayload = {
  field: string
  operator: string
  value: string
  connector?: "AND" | "OR"
}

type ProcessorPayload = {
  processor: {
    id: string
  }
  processorId: string
  target: number
  routingByTarget: boolean
}

export type SmartRoutingConfigurationPayload = {
  id?: string
  whiteLabelId: string
  paymentMethod: string
  period: "DAILY" | "WEEKLY" | "MONTHLY" | "YEARLY"
  status: "ACTIVE" | "INACTIVE"
  firstProcessor: ProcessorPayload
  secondProcessor?: ProcessorPayload
  rules?: RulePayload[]
}

export const useCreateSmartRoutingConfiguration = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: async (
      payload: SmartRoutingConfigurationPayload
    ) => {
      const { data } = await api.post(
        ENDPOINTS.SMART_ROUTING_CONFIGURATIONS,
        payload
      )
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [ENDPOINTS.SMART_ROUTING_CONFIGURATIONS],
      })
      toast({
        title: t("pages.smartRouting.form.createSuccess"),
        variant: "default",
      })
    },
    onError: (error: ErrorType) => {
      toast({
        title:
          error?.message ||
          t("pages.smartRouting.form.createError"),
        variant: "destructive",
      })
    },
  })
}

export const useUpdateSmartRoutingConfiguration = (
  uuid: string
) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: async (
      payload: SmartRoutingConfigurationPayload
    ) => {
      const { data } = await api.put(
        `${ENDPOINTS.SMART_ROUTING_CONFIGURATIONS}/${uuid}`,
        payload
      )
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [ENDPOINTS.SMART_ROUTING_CONFIGURATIONS],
      })
      queryClient.invalidateQueries({
        queryKey: ["smart-routing-configuration", uuid],
      })
      toast({
        title: t("pages.smartRouting.form.updateSuccess"),
        variant: "default",
      })
    },
    onError: (error: ErrorType) => {
      toast({
        title:
          error?.message ||
          t("pages.smartRouting.form.updateError"),
        variant: "destructive",
      })
    },
  })
}
